import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Icon } from '../../../components';
import DocViewer from './DocViewer';
import { getDocument } from '../../services/DocuMateAPI';
import './DocumentPreviewIcon.scss';

interface DocumentPreviewIconProps {
    docFileId?: string;
    scanId?: string;
}

const DocumentPreviewIcon: React.FC<DocumentPreviewIconProps> = ({ docFileId, scanId }) => {
    const [showPreview, setShowPreview] = useState(false);
    const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
    const [hasError, setHasError] = useState(false);
    const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});
    const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const iconRef = useRef<HTMLSpanElement>(null);
    const fetchedRef = useRef(false);

    const cancelHide = () => {
        if (hideTimerRef.current) {
            clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
        }
    };

    const scheduleHide = () => {
        cancelHide();
        hideTimerRef.current = setTimeout(() => {
            setShowPreview(false);
        }, 150);
    };

    const handleMouseEnter = () => {
        cancelHide();
        if (showTimerRef.current) clearTimeout(showTimerRef.current);
        showTimerRef.current = setTimeout(async () => {
            if (iconRef.current) {
                const rect = iconRef.current.getBoundingClientRect();
                // Use clientWidth to exclude scrollbar width, preventing right-overflow
                const viewportWidth = document.documentElement.clientWidth;
                const popoverWidth = 672;
                const leftPos = Math.max(8, rect.left + popoverWidth > viewportWidth
                    ? viewportWidth - popoverWidth - 8
                    : rect.left);
                setPopoverStyle({
                    position: 'fixed',
                    bottom: 10,
                    left: leftPos,
                    zIndex: 99999,
                });
            }
            if (docFileId && !fetchedRef.current) {
                fetchedRef.current = true;
                try {
                    const result = await getDocument(docFileId, true);
                    setPdfBlob(result.blob);
                    setShowPreview(true);
                } catch {
                    setHasError(true);
                }
            } else {
                setShowPreview(true);
            }
        }, 400);
    };

    const handleMouseLeave = () => {
        if (showTimerRef.current) {
            clearTimeout(showTimerRef.current);
            showTimerRef.current = null;
        }
        scheduleHide();
    };

    return (
        <span
            ref={iconRef}
            className="doc-preview-icon-wrapper"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <Icon
                icon="eye"
                className="doc-preview-eye-icon"
                title=""
            />
            {showPreview && pdfBlob && !hasError && ReactDOM.createPortal(
                <div
                    className="doc-preview-popover"
                    style={popoverStyle}
                    onMouseEnter={cancelHide}
                    onMouseLeave={scheduleHide}
                >
                    <div className="doc-preview-viewer-wrapper">
                        <DocViewer document={pdfBlob} scanId={scanId || ''} hidePagination initialScale={0.9} />
                    </div>
                </div>,
                document.body
            )}
        </span>
    );
};

export default DocumentPreviewIcon;
